/**
 * Register Page Component
 * Handles new user registration with optional survey and admin approval workflow
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Form, Input, Button, Alert, Typography, Divider, Select, Checkbox, 
  Steps, Card, Spin, Progress, Radio, Slider, Image 
} from 'antd';
import { 
  UserOutlined, MailOutlined, CheckCircleOutlined, FileTextOutlined, 
  FormOutlined, LeftOutlined, RightOutlined 
} from '@ant-design/icons';
import { useAuth } from '../../context/AuthContext';
import PagesHeader from '../../PagesHeader';
import { getCountriesWithRegistrationSurveys } from '../../services/countriesService';
import surveysService, { ChoiceType } from '../../services/surveysService';
import styles from './Register.module.css';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { Step } = Steps;

// Available languages for preferred language selection (same as Users admin page)
const availableLanguages = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'Deutsch' },
  { value: 'nl', label: 'Nederlands' },
  { value: 'fr', label: 'Français' },
];

// Helper to get valid base language code from i18n.language (e.g., 'en-US' -> 'en')
const getBaseLanguage = (langCode) => {
  const base = langCode?.split('-')[0]?.toLowerCase() || 'en';
  return availableLanguages.some(l => l.value === base) ? base : 'en';
};

/**
 * Determines the question type from its choices
 * Returns: 'freetext' | 'range' | 'single' | 'multiple' | 'mixed'
 */
const getQuestionType = (question) => {
  if (!question?.choices?.length) return 'freetext';
  
  // Check if ALL choices are FreeText - only then it's a pure freetext question
  const allFreeText = question.choices.every(c => c.choiceType === ChoiceType.FreeText);
  if (allFreeText) return 'freetext';
  
  // Check if ALL choices are Range
  const allRange = question.choices.every(c => c.choiceType === ChoiceType.Range);
  if (allRange) return 'range';
  
  // Check if there are mixed choice types (normal + freetext or normal + range)
  const hasNormal = question.choices.some(c => c.choiceType === ChoiceType.Normal);
  const hasFreeText = question.choices.some(c => c.choiceType === ChoiceType.FreeText);
  const hasRange = question.choices.some(c => c.choiceType === ChoiceType.Range);
  
  if ((hasNormal && hasFreeText) || (hasNormal && hasRange) || (hasFreeText && hasRange)) {
    return 'mixed';
  }
  
  // Pure normal choices - render as single/multiple choice
  return question.allowMultipleChoices ? 'multiple' : 'single';
};

/**
 * Helper to determine individual choice type
 */
const getChoiceType = (choice) => {
  const raw = choice?.choiceType;
  const str = String(raw).toLowerCase();
  if (raw === 1 || str === 'range' || str === '1' || raw === ChoiceType.Range) return 'range';
  if (raw === 2 || str === 'freetext' || str === '2' || raw === ChoiceType.FreeText) return 'freetext';
  return 'normal';
};

/**
 * Registration page with form for creating new accounts
 * Step 1: Fill in profile info, accept terms
 * Step 2: (Optional) Select country for survey language, fill survey one question at a time
 * Step 3: Submit and await approval
 */
const Register = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { register, isAuthenticated, loading, error, clearError } = useAuth();
  
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [registerError, setRegisterError] = useState(null);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  
  // Step 1 data
  const [formData, setFormData] = useState(null);
  
  // Step 2 - Survey
  const [countriesWithSurveys, setCountriesWithSurveys] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [loadingSurvey, setLoadingSurvey] = useState(false);
  const [surveyParticipantId, setSurveyParticipantId] = useState(null);
  const [surveyResponses, setSurveyResponses] = useState({});
  const [surveySubmitting, setSurveySubmitting] = useState(false);
  const [surveyCompleted, setSurveyCompleted] = useState(false);
  
  // Survey wizard - question by question
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [surveyStarted, setSurveyStarted] = useState(false);

  // Sorted questions for the survey
  const sortedQuestions = useMemo(() => {
    if (!survey?.questions) return [];
    return [...survey.questions].sort((a, b) => a.orderIndex - b.orderIndex);
  }, [survey]);

  const currentQuestion = sortedQuestions[currentQuestionIndex];
  const totalQuestions = sortedQuestions.length;
  const progressPercent = totalQuestions > 0 ? Math.round(((currentQuestionIndex + 1) / totalQuestions) * 100) : 0;

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Clear errors when component mounts or unmounts
  useEffect(() => {
    clearError();
    return () => clearError();
  }, [clearError]);

  // Sync context error with local state
  useEffect(() => {
    if (error) {
      setRegisterError(error);
    }
  }, [error]);

  // Load countries with registration surveys
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const countries = await getCountriesWithRegistrationSurveys({ suppressNotification: true });
        setCountriesWithSurveys(countries || []);
      } catch (err) {
        console.error('Failed to load countries:', err);
      } finally {
        setLoadingCountries(false);
      }
    };
    loadCountries();
  }, []);

  // Load survey when country is selected
  const handleCountrySelect = useCallback(async (countryId) => {
    setSelectedCountry(countryId);
    setSurvey(null);
    setSurveyParticipantId(null);
    setSurveyResponses({});
    setSurveyCompleted(false);
    setSurveyStarted(false);
    setCurrentQuestionIndex(0);

    if (!countryId) return;

    const country = countriesWithSurveys.find(c => c.id === countryId);
    if (!country?.registrationSurveyId) return;

    setLoadingSurvey(true);
    try {
      // Get survey details
      const surveyData = await surveysService.getPublicSurvey(country.registrationSurveyId);
      setSurvey(surveyData);

      // Start participation (anonymous, using email as identifier)
      const email = form.getFieldValue('email') || formData?.email;
      if (email) {
        const participant = await surveysService.startParticipation(
          country.registrationSurveyId, 
          { participantIdentifier: email }
        );
        setSurveyParticipantId(participant.participantId);
      }
    } catch (err) {
      console.error('Failed to load survey:', err);
      setRegisterError(t('auth.surveyLoadError'));
    } finally {
      setLoadingSurvey(false);
    }
  }, [countriesWithSurveys, form, formData, t]);

  /**
   * Handle Step 1 completion - profile info
   */
  const handleStep1Submit = async (values) => {
    setFormData(values);
    
    // If no countries have surveys, skip step 2
    if (countriesWithSurveys.length === 0) {
      await submitRegistration(values, null);
    } else {
      setCurrentStep(1);
    }
  };

  /**
   * Start the survey wizard
   */
  const handleStartSurvey = () => {
    setSurveyStarted(true);
    setCurrentQuestionIndex(0);
  };

  /**
   * Handle survey response change
   * Supports:
   * - choice/multiChoice: Normal choice selection
   * - textValues: Keyed by choice ID for FreeText choices
   * - rangeValues: Keyed by choice ID for Range choices
   */
  const handleSurveyResponseChange = (questionId, value, responseType = 'text', choiceId = null) => {
    setSurveyResponses(prev => {
      const existing = prev[questionId] || {};
      switch (responseType) {
        case 'choice':
          return { ...prev, [questionId]: { ...existing, choiceId: value } };
        case 'multiChoice':
          return { ...prev, [questionId]: { ...existing, choiceIds: value, isMultiple: true } };
        case 'textValue':
          // FreeText value keyed by choice ID
          const newTextValues = { ...(existing.textValues || {}), [choiceId]: value };
          return { ...prev, [questionId]: { ...existing, textValues: newTextValues } };
        case 'rangeValue':
          // Range value keyed by choice ID
          const newRangeValues = { ...(existing.rangeValues || {}), [choiceId]: value };
          return { ...prev, [questionId]: { ...existing, rangeValues: newRangeValues } };
        case 'text':
        default:
          // Legacy: single textValue (for pure freetext questions)
          return { ...prev, [questionId]: { ...existing, textValue: value } };
      }
    });
  };

  /**
   * Check if current question has a valid response
   * Uses the same logic as Survey page's canProceed function
   */
  const isCurrentQuestionAnswered = () => {
    if (!currentQuestion) return false;
    if (!currentQuestion.isRequired) return true;
    
    const response = surveyResponses[currentQuestion.id];
    if (!response) return false;
    
    // Check if ANY type of answer is provided:
    // 1. Normal choices (single or multiple)
    const hasNormalAnswer = response.isMultiple 
      ? (response.choiceIds && response.choiceIds.length > 0)
      : (response.choiceId != null);
    
    // 2. Range choices - any range value answered
    const hasRangeAnswer = response.rangeValues && Object.keys(response.rangeValues).length > 0;
    
    // 3. FreeText choices - any non-empty text value (keyed by choice ID)
    const hasFreeTextAnswer = response.textValues && 
      Object.values(response.textValues).some(v => v && v.trim().length > 0);
    
    // 4. Legacy single textValue (for pure freetext questions)
    const hasLegacyTextAnswer = response.textValue && response.textValue.trim().length > 0;
    
    // For required questions, at least one type of answer must be provided
    return hasNormalAnswer || hasRangeAnswer || hasFreeTextAnswer || hasLegacyTextAnswer;
  };

  /**
   * Navigate to next question or submit
   */
  const handleNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else {
      // Last question - submit survey
      handleSubmitSurvey();
    }
  };

  /**
   * Navigate to previous question
   */
  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  /**
   * Submit survey responses
   * Formats responses similar to Survey page's handleComplete
   */
  const handleSubmitSurvey = async () => {
    if (!survey || !surveyParticipantId) return;

    setSurveySubmitting(true);
    try {
      // Build all answers from state - a question may have multiple choice types
      const formattedResponses = [];
      
      Object.entries(surveyResponses).forEach(([questionId, response]) => {
        const qId = parseInt(questionId);
        
        // Handle normal choices (single or multiple select)
        if (response.choiceId || (response.choiceIds && response.choiceIds.length > 0)) {
          formattedResponses.push({
            questionId: qId,
            choiceId: response.isMultiple ? undefined : response.choiceId,
            choiceIds: response.isMultiple ? response.choiceIds : undefined,
          });
        }
        
        // Handle range choices - each range choice generates an answer
        if (response.rangeValues) {
          Object.entries(response.rangeValues).forEach(([choiceId, value]) => {
            formattedResponses.push({
              questionId: qId,
              choiceId: parseInt(choiceId),
              numericValue: value,
            });
          });
        }
        
        // Handle freetext choices - each freetext choice generates an answer (keyed by choice ID)
        if (response.textValues) {
          Object.entries(response.textValues).forEach(([choiceId, value]) => {
            if (value && value.trim()) {
              formattedResponses.push({
                questionId: qId,
                choiceId: parseInt(choiceId),
                textValue: value,
              });
            }
          });
        }
        
        // Handle legacy single textValue (for pure freetext questions)
        if (response.textValue && !response.textValues) {
          formattedResponses.push({
            questionId: qId,
            textValue: response.textValue,
          });
        }
      });

      await surveysService.submitSurveyResponse(survey.id, surveyParticipantId, formattedResponses);

      setSurveyCompleted(true);
    } catch (err) {
      console.error('Failed to submit survey:', err);
      setRegisterError(err.message || t('auth.surveySubmitError'));
    } finally {
      setSurveySubmitting(false);
    }
  };

  /**
   * Skip survey and proceed to registration
   */
  const handleSkipSurvey = () => {
    submitRegistration(formData, null);
  };

  /**
   * Proceed after survey completion
   */
  const handleProceedAfterSurvey = () => {
    submitRegistration(formData, surveyParticipantId);
  };

  /**
   * Submit final registration
   */
  const submitRegistration = async (data, participantId) => {
    setSubmitting(true);
    setRegisterError(null);

    try {
      const result = await register({
        email: data.email,
        firstName: data.firstName || undefined,
        lastName: data.lastName || undefined,
        preferredLanguage: data.preferredLanguage || i18n.language,
        termsAccepted: true,
        surveyParticipantId: participantId || undefined,
      });
      
      if (result.success) {
        setRegistrationSuccess(true);
      } else {
        setRegisterError(result.message || t('auth.registrationFailed'));
      }
    } catch (err) {
      setRegisterError(err.message || t('auth.registrationFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Render a single normal choice (radio or checkbox)
   */
  const renderNormalChoice = (question, response, choice, isMultiple, selectedIds) => {
    const isSelected = isMultiple 
      ? selectedIds.includes(choice.id) 
      : response.choiceId === choice.id;

    const handleClick = () => {
      if (surveySubmitting) return;
      if (isMultiple) {
        const newIds = selectedIds.includes(choice.id)
          ? selectedIds.filter(id => id !== choice.id)
          : [...selectedIds, choice.id];
        handleSurveyResponseChange(question.id, newIds, 'multiChoice');
      } else {
        handleSurveyResponseChange(question.id, choice.id, 'choice');
      }
    };

    return (
      <div
        key={choice.id}
        className={`${styles.choiceOption} ${isSelected ? styles.selected : ''}`}
        onClick={handleClick}
        style={{ cursor: surveySubmitting ? 'not-allowed' : 'pointer' }}
      >
        {isMultiple ? (
          <Checkbox checked={isSelected} disabled={surveySubmitting} />
        ) : (
          <Radio checked={isSelected} disabled={surveySubmitting} />
        )}
        <div className={styles.choiceContent}>
          {choice.imageUrl && (
            <Image 
              src={choice.imageUrl} 
              alt="" 
              width={128} height={128}
              onClick={(e) => e.stopPropagation()} 
              preview={{ mask: null }}
            />
          )}
          <span className={styles.choiceText}>{choice.text}</span>
        </div>
      </div>
    );
  };

  /**
   * Render a single range choice as slider
   */
  const renderRangeChoice = (question, response, choice) => {
    const rangeAnswers = response.rangeValues || {};
    const min = choice.rangeMin ?? 0;
    const max = choice.rangeMax ?? 100;
    const value = rangeAnswers[choice.id] ?? min;
    const step = choice.rangeStep || 1;
    
    const minLabel = choice.rangeMinLabel || min;
    const maxLabel = choice.rangeMaxLabel || max;
    
    return (
      <div key={choice.id} className={styles.rangeContainer}>
        {choice.text && (
          <div className={styles.rangeHeader}>
            <Text className={styles.rangeLabel}>{choice.text}</Text>
          </div>
        )}
        <Slider
          min={min}
          max={max}
          step={step}
          value={value}
          tooltip={{ formatter: (val) => val }}
          onChange={(val) => handleSurveyResponseChange(question.id, val, 'rangeValue', choice.id)}
          disabled={surveySubmitting}
        />
        <div className={styles.rangeLabels}>
          <div className={styles.rangeScaleLabel}>
            <Text type="secondary">{min}</Text>
            {minLabel !== min && <Text type="secondary" className={styles.rangeScaleText}>{minLabel}</Text>}
          </div>
          <div className={styles.rangeScaleLabel}>
            <Text type="secondary">{max}</Text>
            {maxLabel !== max && <Text type="secondary" className={styles.rangeScaleText}>{maxLabel}</Text>}
          </div>
        </div>
      </div>
    );
  };

  /**
   * Render a single free text choice as text area
   */
  const renderFreeTextChoice = (question, response, choice) => {
    const textAnswers = response.textValues || {};
    const value = textAnswers[choice.id] || '';
    const maxChars = choice.maxCharacters || 1000;
    const charCount = value.length;

    return (
      <div key={choice.id} className={styles.freeTextContainer}>
        {choice.text && <Text className={styles.freeTextLabel}>{choice.text}</Text>}
        <Input.TextArea
          value={value}
          onChange={(e) => handleSurveyResponseChange(question.id, e.target.value, 'textValue', choice.id)}
          placeholder={choice.placeholder || t('auth.enterYourAnswer')}
          rows={4}
          maxLength={maxChars}
          disabled={surveySubmitting}
          className={styles.surveyInput}
        />
        <div className={styles.charCount}>
          <Text type={charCount > maxChars * 0.9 ? (charCount >= maxChars ? 'danger' : 'warning') : 'secondary'}>
            {charCount} / {maxChars}
          </Text>
        </div>
      </div>
    );
  };

  /**
   * Render all choices for a question - each choice rendered by its own type
   * This is the key fix: FreeText and Range choices render as their own input types,
   * not as selectable radio/checkbox options
   */
  const renderChoices = (question, response) => {
    const choices = question.choices || [];
    const isMultiple = question.allowMultipleChoices;
    const selectedIds = isMultiple ? (response.choiceIds || []) : [];

    return (
      <div className={styles.choicesContainer}>
        {isMultiple && (
          <div className={styles.multiSelectHint}>
            <Text type="secondary">{t('surveys.selectMultiple', 'Select all that apply')}</Text>
          </div>
        )}
        {choices.map((choice, index) => {
          const choiceType = getChoiceType(choice);
          
          if (choiceType === 'range') {
            return (
              <div key={choice.id} className={index > 0 ? styles.choiceSpacing : undefined}>
                {renderRangeChoice(question, response, choice)}
              </div>
            );
          }
          
          if (choiceType === 'freetext') {
            return (
              <div key={choice.id} className={index > 0 ? styles.choiceSpacing : undefined}>
                {renderFreeTextChoice(question, response, choice)}
              </div>
            );
          }
          
          // Normal choice - render as individual selectable option
          return renderNormalChoice(question, response, choice, isMultiple, selectedIds);
        })}
      </div>
    );
  };

  /**
   * Render the current question's input control
   * Uses renderChoices to properly handle mixed choice types
   */
  const renderQuestionInput = () => {
    if (!currentQuestion) return null;
    
    const response = surveyResponses[currentQuestion.id] || {};
    const hasChoices = currentQuestion.choices && currentQuestion.choices.length > 0;
    
    if (!hasChoices) {
      // No choices - render a simple text area
      return (
        <Input.TextArea
          rows={4}
          placeholder={t('auth.enterYourAnswer')}
          value={response.textValue || ''}
          onChange={(e) => handleSurveyResponseChange(currentQuestion.id, e.target.value, 'text')}
          disabled={surveySubmitting}
          className={styles.surveyInput}
        />
      );
    }
    
    // Render choices - the renderChoices function handles all choice types
    return renderChoices(currentQuestion, response);
  };

  // Show success message after registration
  if (registrationSuccess) {
    return (
      <div className={styles.pageContainer}>
        <PagesHeader />
        <main className={styles.main}>
          <div className={styles.registerCard}>
            <div className={styles.successContainer}>
              <CheckCircleOutlined className={styles.successIcon} />
              <Title level={3} className={styles.successTitle}>
                {t('auth.registrationSubmitted')}
              </Title>
              <Paragraph className={styles.successText}>
                {t('auth.registrationPendingApproval')}
              </Paragraph>
              <Paragraph className={styles.successText}>
                {t('auth.confirmEmailInstructions')}
              </Paragraph>
              <Link to="/login">
                <Button type="primary" size="large" className={styles.submitButton}>
                  {t('auth.goToLogin')}
                </Button>
              </Link>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Survey completed, ready to submit registration
  if (currentStep === 1 && surveyCompleted) {
    return (
      <div className={styles.pageContainer}>
        <PagesHeader />
        <main className={styles.main}>
          <div className={styles.registerCard}>
            <div className={styles.successContainer}>
              <CheckCircleOutlined className={styles.successIcon} style={{ color: '#52c41a' }} />
              <Title level={3} className={styles.successTitle}>
                {t('auth.surveyCompleted')}
              </Title>
              <Text className={styles.successText}>
                {t('auth.surveyThankYou')}
              </Text>
              <Button
                type="primary"
                size="large"
                onClick={handleProceedAfterSurvey}
                loading={submitting}
                className={styles.submitButton}
              >
                {t('auth.completeRegistration')}
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      <PagesHeader />
      
      <main className={styles.main}>
        <div className={styles.registerCard}>
          {countriesWithSurveys.length > 0 && (
            <Steps current={currentStep} className={styles.steps} size="small">
              <Step title={t('auth.stepProfile')} icon={<UserOutlined />} />
              <Step title={t('auth.stepSurvey')} icon={<FormOutlined />} />
            </Steps>
          )}

          {registerError && (
            <Alert
              message={registerError}
              type="error"
              showIcon
              closable
              onClose={() => setRegisterError(null)}
              className={styles.alert}
            />
          )}

          {/* Step 1: Profile Information */}
          {currentStep === 0 && (
            <>
              <div className={styles.header}>
                <Title level={2} className={styles.title}>
                  {t('auth.createYourAccount')}
                </Title>
                <Text type="secondary" className={styles.subtitle}>
                  {t('auth.joinHeatCraftHP')} <span className={styles.brandAccent}>HeatCraftHP</span>
                </Text>
              </div>

              <Form
                form={form}
                name="register"
                onFinish={handleStep1Submit}
                layout="vertical"
                requiredMark={false}
                className={styles.form}
                initialValues={{ preferredLanguage: getBaseLanguage(i18n.language) }}
              >
                <div className={styles.nameRow}>
                  <Form.Item
                    name="firstName"
                    label={t('auth.firstName')}
                    className={styles.nameField}
                  >
                    <Input
                      prefix={<UserOutlined className={styles.inputIcon} />}
                      placeholder={t('auth.firstNamePlaceholder')}
                      size="large"
                      autoComplete="given-name"
                      disabled={submitting}
                    />
                  </Form.Item>

                  <Form.Item
                    name="lastName"
                    label={t('auth.lastName')}
                    className={styles.nameField}
                  >
                    <Input
                      prefix={<UserOutlined className={styles.inputIcon} />}
                      placeholder={t('auth.lastNamePlaceholder')}
                      size="large"
                      autoComplete="family-name"
                      disabled={submitting}
                    />
                  </Form.Item>
                </div>

                <Form.Item
                  name="email"
                  label={t('auth.email')}
                  rules={[
                    { required: true, message: t('auth.emailRequired') },
                    { type: 'email', message: t('auth.emailInvalid') }
                  ]}
                >
                  <Input
                    prefix={<MailOutlined className={styles.inputIcon} />}
                    placeholder={t('auth.emailPlaceholder')}
                    size="large"
                    autoComplete="email"
                    disabled={submitting}
                  />
                </Form.Item>

                <Form.Item
                  name="preferredLanguage"
                  label={t('auth.preferredLanguage')}
                >
                  <Select 
                    size="large" 
                    disabled={submitting}
                    options={availableLanguages}
                  />
                </Form.Item>

                <Form.Item
                  name="termsAccepted"
                  valuePropName="checked"
                  rules={[
                    {
                      validator: (_, value) =>
                        value ? Promise.resolve() : Promise.reject(new Error(t('auth.termsRequired'))),
                    },
                  ]}
                >
                  <Checkbox disabled={submitting}>
                    {t('auth.iAcceptThe')}{' '}
                    <Link to="/terms" target="_blank" className={styles.termsLink}>
                      {t('auth.termsAndConditions')}
                    </Link>
                  </Checkbox>
                </Form.Item>

                <div className={styles.infoBox}>
                  <FileTextOutlined className={styles.infoIcon} />
                  <Text type="secondary">
                    {t('auth.approvalProcessInfo')}
                  </Text>
                </div>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={submitting || loading}
                    className={styles.submitButton}
                  >
                    {countriesWithSurveys.length > 0 
                      ? t('auth.continueToSurvey')
                      : t('auth.submitRegistration')
                    }
                  </Button>
                </Form.Item>
              </Form>

              <Divider plain className={styles.divider}>
                {t('auth.or')}
              </Divider>

              <div className={styles.loginPrompt}>
                <Text type="secondary">
                  {t('auth.alreadyHaveAccount')}{' '}
                </Text>
                <Link to="/login" className={styles.loginLink}>
                  {t('auth.signIn')}
                </Link>
              </div>
            </>
          )}

          {/* Step 2: Optional Survey */}
          {currentStep === 1 && !surveyCompleted && (
            <>
              {/* Survey Intro - Country Selection */}
              {!surveyStarted && (
                <>
                  <div className={styles.header}>
                    <Title level={2} className={styles.title}>
                      {t('auth.optionalSurvey')}
                    </Title>
                    <Text type="secondary" className={styles.subtitle}>
                      {t('auth.surveyDescription')}
                    </Text>
                  </div>

                  {loadingCountries ? (
                    <div className={styles.loadingContainer}>
                      <Spin size="large" />
                    </div>
                  ) : (
                    <>
                      <Form.Item label={t('auth.selectCountryForSurvey')}>
                        <Select
                          placeholder={t('auth.selectCountryPlaceholder')}
                          size="large"
                          allowClear
                          onChange={handleCountrySelect}
                          value={selectedCountry}
                          disabled={surveySubmitting}
                        >
                          {countriesWithSurveys.map(country => (
                            <Option key={country.id} value={country.id}>
                              {country.flagImageUrl && (
                                <img 
                                  src={country.flagImageUrl} 
                                  alt="" 
                                  style={{ width: 20, height: 14, marginRight: 8, verticalAlign: 'middle' }} 
                                />
                              )}
                              {country.name} ({country.language})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>

                      {loadingSurvey && (
                        <div className={styles.loadingContainer}>
                          <Spin size="large" />
                        </div>
                      )}

                      {survey && !loadingSurvey && (
                        <Card className={styles.surveyIntroCard}>
                          <Title level={4}>{survey.name}</Title>
                          {survey.introText && (
                            <Paragraph type="secondary">{survey.introText}</Paragraph>
                          )}
                          <div className={styles.surveyMeta}>
                            <Text type="secondary">
                              {t('auth.surveyQuestionCount', { count: totalQuestions })}
                            </Text>
                          </div>
                          <Button
                            type="primary"
                            size="large"
                            onClick={handleStartSurvey}
                            className={styles.submitButton}
                            icon={<FormOutlined />}
                          >
                            {t('auth.startSurvey')}
                          </Button>
                        </Card>
                      )}

                      <div className={styles.surveyActions}>
                        <Button 
                          onClick={() => setCurrentStep(0)} 
                          disabled={surveySubmitting}
                        >
                          {t('common.back')}
                        </Button>
                        <Button 
                          type="default" 
                          onClick={handleSkipSurvey}
                          loading={submitting}
                          disabled={surveySubmitting}
                        >
                          {t('auth.skipSurvey')}
                        </Button>
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Survey Question Wizard */}
              {surveyStarted && currentQuestion && (
                <div className={styles.surveyWizard}>
                  {/* Survey Header with Intro Text */}
                  {survey?.introText && currentQuestionIndex === 0 && (
                    <div className={styles.surveyIntroText}>
                      <Paragraph type="secondary">{survey.introText}</Paragraph>
                    </div>
                  )}

                  {/* Progress Header */}
                  <div className={styles.surveyProgressHeader}>
                    <Text type="secondary">
                      {t('auth.questionProgress', { current: currentQuestionIndex + 1, total: totalQuestions })}
                    </Text>
                    <Progress 
                      percent={progressPercent} 
                      showInfo={false} 
                      strokeColor="#1890ff"
                      className={styles.progressBar}
                    />
                  </div>

                  {/* Question Card */}
                  <Card className={styles.questionCard}>
                    <div className={styles.questionHeader}>
                      <Title level={4} className={styles.questionTitle}>
                        {currentQuestion.name}
                        {currentQuestion.isRequired && <span className={styles.required}> *</span>}
                      </Title>
                      {currentQuestion.body && (
                        <Paragraph type="secondary" className={styles.questionBody}>
                          {currentQuestion.body}
                        </Paragraph>
                      )}
                      {currentQuestion.imageUrl && (
                        <Image 
                          src={currentQuestion.imageUrl} 
                          alt="" 
                          className={styles.questionImage}
                          preview={{ mask: null }}
                        />
                      )}
                    </div>

                    <div className={styles.questionContent}>
                      {renderQuestionInput()}
                    </div>
                  </Card>

                  {/* Navigation */}
                  <div className={styles.surveyNavigation}>
                    <Button
                      icon={<LeftOutlined />}
                      onClick={handlePrevQuestion}
                      disabled={currentQuestionIndex === 0 || surveySubmitting}
                    >
                      {t('common.previous')}
                    </Button>
                    
                    <Button
                      type="primary"
                      onClick={handleNextQuestion}
                      disabled={!isCurrentQuestionAnswered() || surveySubmitting}
                      loading={surveySubmitting && currentQuestionIndex === totalQuestions - 1}
                    >
                      {currentQuestionIndex === totalQuestions - 1 
                        ? t('auth.submitSurvey')
                        : t('common.next')
                      }
                      {currentQuestionIndex < totalQuestions - 1 && <RightOutlined />}
                    </Button>
                  </div>

                  {/* Skip option */}
                  <div className={styles.skipSurveyLink}>
                    <Button 
                      type="link" 
                      onClick={handleSkipSurvey}
                      disabled={surveySubmitting}
                    >
                      {t('auth.skipAndRegister')}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Register;
